import {
  ApplicationFormSchema,
  BasicDataFormSchema,
  GeneralPhysicianFormSchema,
  InsuranceCompanyFormSchema,
  MedicineFormSchema,
} from '@/modules/basic-data/schemas'
import {BasicDataFormProps} from '@/modules/basic-data/types'
import {
  ApplicationForm,
  BasicDataItem,
  BasicDataResource,
  BasicDataResources,
  GeneralPhysician,
  InsuranceCompany,
  MedicineForm,
} from '@cypro/engine/schema'

type BasicDataFormItem = BasicDataFormProps['item']

export function getBasicDataDefaultFormValues(item: Partial<BasicDataItem> | undefined) {
  let basicDataItem:
    | BasicDataFormSchema
    | MedicineFormSchema
    | ApplicationFormSchema
    | InsuranceCompanyFormSchema
    | GeneralPhysicianFormSchema = {
    code: item?.code || '',
    name: item?.name || '',
    description: item?.description || '',
    status: item?.status || 'active',
  }

  if (isMedicineForm(item)) {
    basicDataItem = {
      ...basicDataItem,
      reconstitutionRequired: Boolean(item.reconstitutionRequired),
      isDrySubstance: Boolean(item.isDrySubstance),
    }
  }

  if (isApplicationForm(item)) {
    basicDataItem = {
      ...basicDataItem,
      preparationRequired: Boolean(item.preparationRequired),
      carrierRequired: Boolean(item.carrierRequired),
      machineId: '',
    }
  }

  if (isInsuranceCompany(item)) {
    basicDataItem = {
      ...basicDataItem,
      nationalId: item.nationalId || '',
    }
  }

  if (isGeneralPhysician(item)) {
    basicDataItem = {
      ...basicDataItem,
      specialty: item.specialty || '',
      phone: item.phone || '',
      email: item.email || '',
    }
  }

  return basicDataItem
}

export function getBasicDataFormSchema(resource: BasicDataResource | undefined) {
  if (resource === BasicDataResources.medicineForms) return MedicineFormSchema
  if (resource === BasicDataResources.applicationForms) return ApplicationFormSchema
  if (resource === BasicDataResources.insuranceCompanies) return InsuranceCompanyFormSchema
  if (resource === BasicDataResources.generalPhysicians) return GeneralPhysicianFormSchema
  return BasicDataFormSchema
}

export function isInsuranceCompany(resource: unknown): resource is InsuranceCompany {
  if (!resource) return false
  const casted = resource as InsuranceCompany

  return 'nationalId' in casted
}

export function isGeneralPhysician(resource: unknown): resource is GeneralPhysician {
  if (!resource) return false
  const casted = resource as GeneralPhysician

  return 'specialty' in casted
}

export function isValidResource(resource: unknown): resource is BasicDataResource {
  if (typeof resource !== 'string') return false
  const resources = Object.values(BasicDataResources) as string[]
  return resources.includes(resource)
}

export function isMedicineForm(resource: unknown): resource is MedicineForm {
  if (!resource) return false
  const casted = resource as MedicineForm

  // the two surviving medicine-form-only columns double as the type guard
  return 'reconstitutionRequired' in casted && 'isDrySubstance' in casted
}

export function isApplicationForm(resource: unknown): resource is ApplicationForm {
  if (!resource) return false
  const casted = resource as ApplicationForm

  return 'preparationRequired' in casted && 'carrierRequired' in casted
}

/**
 * Detects a stale row being passed with a mismatched resource, using the same
 * duck typing as the `isMedicineForm`/`isApplicationForm` guards. Only meaningful
 * for edits - rows have no fields during creation.
 */
export function isBasicDataShapeMismatch(item: BasicDataFormItem): boolean {
  if (!item?.id) return false

  if (item.resource === BasicDataResources.medicineForms) return !isMedicineForm(item)
  if (item.resource === BasicDataResources.applicationForms) return !isApplicationForm(item)

  return false
}

export function mapBasicDataItemsToSelectItems(items: BasicDataItem[], config?: {includeCode?: boolean}) {
  return items.map(item => {
    const label = [item.name]
    if (item.code && config?.includeCode !== false) {
      label.unshift(item.code)
    }

    return {label: label.join(' - '), value: item.id}
  })
}

export function mapInsuranceCompaniesToSelectItems(companies: InsuranceCompany[]) {
  return companies.map(t => {
    const label = [t.name]
    if (t.nationalId) label.push(t.nationalId)

    return {value: t.id, label: label.join(' - ')}
  })
}

// Separators that don't carry meaning in a short code: "IV-infusion" splits into ["IV", "infusion"] instead of producing a trailing "IV-".
const CODE_WORD_SEPARATOR = /[ \t\-_/(),.&]+/
const CODE_MAX_LENGTH = 4

/**
 * Builds a code from a display name using the initials of each word
 * (all-caps words like "IV" contribute in full). The first non-acronym word
 * is extended with its tail so short names still reach a 4-character code.
 */
export function generateBasicDataCode(name: string | undefined) {
  if (!name) return ''

  const words = name.split(CODE_WORD_SEPARATOR).filter(Boolean)
  if (words.length === 0) return ''

  const wordsAreAcronyms = words.map(word => word.length > 1 && word === word.toUpperCase())

  let code = ''
  for (let index = 0; index < words.length && code.length < CODE_MAX_LENGTH; index++) {
    const word = words[index].replace(/[^A-Za-z]/g, '').toUpperCase()
    if (!word) continue

    if (wordsAreAcronyms[index]) {
      code += word
    } else {
      code += word[0]
      // Extend with the tail of the word ("IV Infusion" -> IVIN, "Intravenous
      // Piggyback" -> INTR) so short names still reach a 4-character code.
      code += word.slice(1, 1 + (CODE_MAX_LENGTH - code.length))
    }
  }

  return code
    .replace(/[^A-Za-z0-9]/g, '')
    .toUpperCase()
    .slice(0, CODE_MAX_LENGTH)
}

interface AutoFillCodePayload {
  isCodePristine: boolean
  name: string | undefined
  code: string | undefined
}
export function autoFillCode({isCodePristine, code, name}: AutoFillCodePayload) {
  if (!isCodePristine) return null
  const built = generateBasicDataCode(name)
  if (code === built) return null
  return built
}
